import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Card, 
  Button, 
  message,
  Descriptions,
  Tag,
  Space,
  Spin,
  Alert,
  Table,
  Progress,
  Tabs,
  Input,
  Select,
  Modal,
  Statistic,
  Row,
  Col,
  Divider,
  Tooltip,
  Badge,
  Empty,
  Tree
} from 'antd';
import { 
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  SearchOutlined,
  SyncOutlined,
  SettingOutlined,
  EyeOutlined,
  CodeOutlined,
  FileOutlined
} from '@ant-design/icons';
import { 
  getKnowledgeStatus, 
  getKnowledgeDocuments,
  getDocumentContent,
  reindexKnowledgeBase 
} from '../services/knowledgeService';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;

function KnowledgeStatus() {
  const [status, setStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState(null);
  const [docModalVisible, setDocModalVisible] = useState(false);
  const [reindexModalVisible, setReindexModalVisible] = useState(false);
  const [reindexProgressVisible, setReindexProgressVisible] = useState(false);
  const [reindexProgress, setReindexProgress] = useState(0);
  const [reindexStatus, setReindexStatus] = useState('');
  const [initialTimestamp, setInitialTimestamp] = useState(null);
  const [reindexConfig, setReindexConfig] = useState({
    folders: [],
    chunk_size: 1000,
    overlap: 150
  });

  useEffect(() => {
    loadStatus();
    loadDocuments();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const data = await getKnowledgeStatus();
      setStatus(data);
    } catch (error) {
      message.error('Failed to load knowledge base status');
      console.error('Error loading status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const data = await getKnowledgeDocuments();
      setDocuments(data.documents || []);
    } catch (error) {
      message.error('Failed to load documents');
      console.error('Error loading documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleViewDocument = async (doc) => {
    setSelectedDoc(doc);
    setDocModalVisible(true);
    setDocContent(null);
    
    try {
      const content = await getDocumentContent(doc.path);
      setDocContent(content);
    } catch (error) {
      message.error('Failed to load document content');
      console.error('Error loading document:', error);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    setReindexStatus('Starting reindexing...');
    setReindexProgress(5);
    
    try {
      // Store the current timestamp before reindexing
      setInitialTimestamp(status?.last_updated);
      
      const result = await reindexKnowledgeBase(reindexConfig);
      message.success('Reindexing started successfully!');
      setReindexModalVisible(false);
      setReindexProgressVisible(true);
      setReindexStatus('Processing documents...');
      setReindexProgress(10);
      
      // Start polling for completion
      pollReindexStatus();
    } catch (error) {
      message.error('Failed to start reindexing');
      console.error('Error reindexing:', error);
      setReindexing(false);
      setReindexProgressVisible(false);
    }
  };

  const pollReindexStatus = async () => {
    let attempts = 0;
    const maxAttempts = 60; // Poll for up to 5 minutes (60 * 5 seconds)
    
    const checkStatus = async () => {
      attempts++;
      
      try {
        const newStatus = await getKnowledgeStatus();
        
        // Update progress based on attempts (simulate progress)
        const progressPercent = Math.min(10 + (attempts * 1.5), 95);
        setReindexProgress(progressPercent);
        setReindexStatus(`Processing... (${attempts * 5}s elapsed)`);
        
        // Check if indexing is complete by comparing timestamp
        if (newStatus.last_updated && newStatus.last_updated !== initialTimestamp) {
          // Indexing complete
          setReindexProgress(100);
          setReindexStatus('Reindexing completed!');
          setStatus(newStatus);
          
          setTimeout(() => {
            setReindexProgressVisible(false);
            setReindexing(false);
            message.success(`Reindexing completed! Indexed ${newStatus.total_documents} documents.`);
            loadStatus();
            loadDocuments();
          }, 1500);
          
          return;
        }
        
        // Continue polling if not done and haven't exceeded max attempts
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000); // Poll every 5 seconds
        } else {
          // Timeout
          setReindexStatus('Reindexing is taking longer than expected...');
          setReindexProgress(95);
          setTimeout(() => {
            setReindexProgressVisible(false);
            setReindexing(false);
            message.warning('Reindexing may still be in progress. Please check the status.');
            loadStatus();
            loadDocuments();
          }, 2000);
        }
      } catch (error) {
        console.error('Error polling status:', error);
        setReindexStatus('Error checking status');
        setTimeout(() => {
          setReindexProgressVisible(false);
          setReindexing(false);
        }, 2000);
      }
    };
    
    // Start the first check after a short delay
    setTimeout(checkStatus, 5000);
  };

  // Get unique categories - moved before conditional returns
  const categories = useMemo(() => {
    return [...new Set(documents.map(d => d.category))].sort();
  }, [documents]);
  
  // Build tree data structure from documents - moved before conditional returns
  const treeData = useMemo(() => {
    if (!documents || documents.length === 0) return [];

    // Filter documents first
    const filtered = documents.filter(doc => {
      const matchesSearch = !searchText || 
        doc.name.toLowerCase().includes(searchText.toLowerCase()) ||
        doc.path.toLowerCase().includes(searchText.toLowerCase());
      const matchesCategory = categoryFilter.length === 0 || categoryFilter.includes(doc.category);
      return matchesSearch && matchesCategory;
    });

    // Build tree structure
    const tree = {};
    
    filtered.forEach(doc => {
      const parts = doc.path.split('/');
      let current = tree;
      
      // Build path hierarchy
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // This is the file
          if (!current._files) current._files = [];
          current._files.push(doc);
        } else {
          // This is a folder
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      });
    });

    // Convert tree object to ant design tree data format
    const convertToTreeNodes = (obj, parentKey = '') => {
      const nodes = [];
      
      // Add folders
      Object.keys(obj).forEach(key => {
        if (key === '_files') return;
        
        const nodeKey = parentKey ? `${parentKey}/${key}` : key;
        const children = convertToTreeNodes(obj[key], nodeKey);
        
        nodes.push({
          title: (
            <Space>
              <FolderOutlined style={{ color: '#faad14' }} />
              <Text strong>{key}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>({children.length})</Text>
            </Space>
          ),
          key: `folder-${nodeKey}`,
          icon: <FolderOutlined />,
          children: children,
          isLeaf: false,
          selectable: false
        });
      });
      
      // Add files
      if (obj._files) {
        obj._files.forEach(file => {
          nodes.push({
            title: (
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space>
                  <FileTextOutlined style={{ color: '#1890ff' }} />
                  <Text>{file.name}</Text>
                </Space>
                <Space>
                  <Tag color="blue">{file.size_kb} KB</Tag>
                  {file.estimated_tokens && (
                    <Tag color="green">{file.estimated_tokens.toLocaleString()} tokens</Tag>
                  )}
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDocument(file);
                    }}
                  >
                    View
                  </Button>
                </Space>
              </Space>
            ),
            key: `file-${file.path}`,
            icon: <FileOutlined />,
            isLeaf: true,
            data: file
          });
        });
      }
      
      return nodes;
    };

    return convertToTreeNodes(tree);
  }, [documents, searchText, categoryFilter]);

  // Auto-expand all folders when filter changes
  useEffect(() => {
    if (searchText || categoryFilter.length > 0) {
      // Extract all folder keys for expansion
      const getAllKeys = (nodes) => {
        let keys = [];
        nodes.forEach(node => {
          if (!node.isLeaf) {
            keys.push(node.key);
            if (node.children) {
              keys = keys.concat(getAllKeys(node.children));
            }
          }
        });
        return keys;
      };
      setExpandedKeys(getAllKeys(treeData));
    }
  }, [searchText, categoryFilter, treeData]);

  // Conditional returns after all hooks
  if (loading && !status) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: '16px' }}>Loading knowledge base status...</Paragraph>
      </div>
    );
  }

  if (!status) {
    return (
      <Alert
        message="No Status Available"
        description="Unable to load knowledge base status. Please try again."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ color: 'var(--theme-primary)', marginBottom: '8px' }}>
            📚 Knowledge Base Management
          </Title>
          <Paragraph style={{ fontSize: '15px', color: '#8c8c8c', marginBottom: 0 }}>
            Browse, preview, and maintain your knowledge base documents and indexes.
          </Paragraph>
        </div>
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => { loadStatus(); loadDocuments(); }}
            loading={loading || documentsLoading}
          >
            Refresh All
          </Button>
          <Button 
            type="primary"
            icon={<SyncOutlined />}
            onClick={() => setReindexModalVisible(true)}
            disabled={!status.is_ready}
          >
            Reindex
          </Button>
        </Space>
      </div>

      <Tabs defaultActiveKey="overview" size="large">
        {/* Overview Tab */}
        <TabPane 
          tab={
            <span>
              <DatabaseOutlined />
              Overview
            </span>
          } 
          key="overview"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Status Overview */}
            <Card>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Status"
                    value={status.is_ready ? "Ready" : "Not Ready"}
                    valueStyle={{ color: status.is_ready ? '#3f8600' : '#cf1322' }}
                    prefix={status.is_ready ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Total Documents"
                    value={status.total_documents || 0}
                    prefix={<FileTextOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Total Chunks"
                    value={status.total_chunks || 0}
                    prefix={<CodeOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Avg Chunks/Doc"
                    value={status.total_documents > 0 
                      ? (status.total_chunks / status.total_documents).toFixed(1)
                      : 0}
                  />
                </Col>
              </Row>
            </Card>

            {/* Configuration Details */}
            <Card title={
              <Space>
                <SettingOutlined />
                <span>Configuration & Metadata</span>
              </Space>
            }>
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Embedding Model" span={2}>
                  <Badge status="success" text={status.embedding_model || 'N/A'} />
                </Descriptions.Item>
                <Descriptions.Item label="Dimensions">
                  {status.embedding_dimensions || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Index Size">
                  {status.index_size_bytes 
                    ? `${(status.index_size_bytes / 1024 / 1024).toFixed(2)} MB`
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Index Location" span={2}>
                  <Text code style={{ fontSize: '11px' }}>{status.index_path || 'N/A'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Last Updated" span={2}>
                  {status.last_updated 
                    ? new Date(status.last_updated * 1000).toLocaleString()
                    : 'N/A'}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Title level={5}>Indexing Options</Title>
              <Descriptions bordered column={3} size="small">
                <Descriptions.Item label="Chunk Size">
                  {status.indexing_options?.chunk_size || 1000} characters
                </Descriptions.Item>
                <Descriptions.Item label="Overlap">
                  {status.indexing_options?.overlap || 150} characters
                </Descriptions.Item>
                <Descriptions.Item label="File Types">
                  {status.indexing_options?.file_types?.join(', ') || '.md, .mdx, .txt'}
                </Descriptions.Item>
              </Descriptions>

              {status.available_folders && status.available_folders.length > 0 && (
                <>
                  <Divider />
                  <Title level={5}>Available Categories</Title>
                  <Space wrap>
                    {status.available_folders.map(folder => (
                      <Tag key={folder} icon={<FolderOutlined />} color="blue">
                        {folder}
                      </Tag>
                    ))}
                  </Space>
                </>
              )}
            </Card>
          </Space>
        </TabPane>

        {/* Documents Tab */}
        <TabPane 
          tab={
            <Badge count={documents.length} offset={[10, 0]}>
              <span>
                <FileTextOutlined />
                Documents
              </span>
            </Badge>
          } 
          key="documents"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Documents File Explorer */}
            <Card title="📁 File Explorer" loading={documentsLoading}>
              {/* Filters */}
              <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col span={12}>
                  <Search
                    placeholder="Search documents..."
                    allowClear
                    prefix={<SearchOutlined />}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={12}>
                  <Select
                    mode="multiple"
                    placeholder="Filter by category"
                    style={{ width: '100%' }}
                    onChange={setCategoryFilter}
                    allowClear
                  >
                    {categories.map(cat => (
                      <Option key={cat} value={cat}>
                        <FolderOutlined /> {cat}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              
              {(searchText || categoryFilter.length > 0) && (
                <Alert
                  message={`Filtered: ${treeData.reduce((acc, node) => {
                    const countFiles = (n) => {
                      let count = n.isLeaf ? 1 : 0;
                      if (n.children) {
                        count += n.children.reduce((c, child) => c + countFiles(child), 0);
                      }
                      return count;
                    };
                    return acc + countFiles(node);
                  }, 0)} of ${documents.length} documents`}
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              {treeData.length === 0 ? (
                <Empty 
                  description={documents.length === 0 ? "No documents found" : "No documents match your filters"}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Tree
                  showIcon
                  showLine
                  defaultExpandAll={false}
                  expandedKeys={expandedKeys}
                  onExpand={(keys) => setExpandedKeys(keys)}
                  treeData={treeData}
                  height={600}
                  style={{
                    backgroundColor: '#fafafa',
                    padding: '16px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
              )}
            </Card>
          </Space>
        </TabPane>
      </Tabs>

      {/* Document Viewer Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            {selectedDoc?.name}
          </Space>
        }
        open={docModalVisible}
        onCancel={() => setDocModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setDocModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        {selectedDoc && (
          <>
            <Descriptions size="small" bordered column={2} style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="Path" span={2}>
                <Text code>{selectedDoc.path}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {selectedDoc.category}
              </Descriptions.Item>
              <Descriptions.Item label="Size">
                {selectedDoc.size_kb} KB
              </Descriptions.Item>
              <Descriptions.Item label="Extension">
                {selectedDoc.extension}
              </Descriptions.Item>
              <Descriptions.Item label="Modified">
                {selectedDoc.modified_readable}
              </Descriptions.Item>
            </Descriptions>

            {docContent ? (
              <div 
                style={{ 
                  maxHeight: '500px', 
                  overflow: 'auto', 
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '13px' }}>
                  {docContent.content}
                </pre>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin tip="Loading document content..." />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Reindex Configuration Modal */}
      <Modal
        title={
          <Space>
            <SyncOutlined />
            Configure Reindexing
          </Space>
        }
        open={reindexModalVisible}
        onCancel={() => setReindexModalVisible(false)}
        onOk={handleReindex}
        confirmLoading={reindexing}
        okText="Start Reindexing"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="Reindex Knowledge Base"
          description="This will rebuild the embedding index. The process runs in the background and may take several minutes depending on the number of documents."
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Select Categories to Index:</Text>
            <div style={{ marginTop: '8px' }}>
              <Select
                mode="multiple"
                placeholder="Select categories (leave empty for all)"
                style={{ width: '100%' }}
                onChange={(folders) => setReindexConfig({ ...reindexConfig, folders })}
                value={reindexConfig.folders}
              >
                {status.available_folders?.map(folder => (
                  <Option key={folder} value={folder}>
                    <FolderOutlined /> {folder}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Text strong>Chunk Size (characters):</Text>
            <Input
              type="number"
              min={500}
              max={2000}
              value={reindexConfig.chunk_size}
              onChange={(e) => setReindexConfig({ ...reindexConfig, chunk_size: parseInt(e.target.value) || 1000 })}
              style={{ marginTop: '8px' }}
            />
          </div>

          <div>
            <Text strong>Overlap (characters):</Text>
            <Input
              type="number"
              min={0}
              max={500}
              value={reindexConfig.overlap}
              onChange={(e) => setReindexConfig({ ...reindexConfig, overlap: parseInt(e.target.value) || 150 })}
              style={{ marginTop: '8px' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Reindex Progress Modal */}
      <Modal
        title={
          <Space>
            <SyncOutlined spin={reindexing} />
            Reindexing in Progress
          </Space>
        }
        open={reindexProgressVisible}
        footer={null}
        closable={false}
        maskClosable={false}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Please Wait"
            description="The knowledge base is being reindexed. This process may take a few minutes depending on the number of documents."
            type="info"
            showIcon
          />

          <div>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <Text strong>{reindexStatus}</Text>
              <Text type="secondary">{Math.round(reindexProgress)}%</Text>
            </div>
            <Progress 
              percent={reindexProgress} 
              status={reindexProgress === 100 ? 'success' : 'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
          </div>

          {reindexConfig.folders && reindexConfig.folders.length > 0 && (
            <div>
              <Text type="secondary">Indexing categories: </Text>
              <div style={{ marginTop: '8px' }}>
                {reindexConfig.folders.map(folder => (
                  <Tag key={folder} icon={<FolderOutlined />} color="blue">
                    {folder}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <Divider style={{ margin: '8px 0' }} />

          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            <Space direction="vertical" size="small">
              <Text type="secondary">• Chunk Size: {reindexConfig.chunk_size} characters</Text>
              <Text type="secondary">• Overlap: {reindexConfig.overlap} characters</Text>
              <Text type="secondary">• This modal will close automatically when complete</Text>
            </Space>
          </div>
        </Space>
      </Modal>
    </div>
  );
}

export default KnowledgeStatus;
